import { useEffect, useRef, useState } from "react";
import Lenis from "@studio-freight/lenis";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { Phone, ChevronRight, CalendarDays, ArrowDown, Paintbrush, Car, Sparkles, Shield, Droplets, Wrench } from "lucide-react";
import { useNavigate } from "react-router-dom";

// Services data
const SERVICES = [
  { icon: Paintbrush, title: "Full Resprays", desc: "Complete colour transformations with factory-quality finish" },
  { icon: Sparkles, title: "Custom Paint", desc: "Unique designs, pearl effects, and show-quality finishes" },
  { icon: Car, title: "Colour Matching", desc: "Precise matching for seamless panel repairs" },
  { icon: Wrench, title: "Dent & Scratch", desc: "Expert bodywork and paint correction" },
  { icon: Shield, title: "Ceramic Coating", desc: "Long-lasting protection with incredible shine" },
  { icon: Droplets, title: "Detailing", desc: "Interior and exterior restoration to showroom condition" },
];

// Portfolio images
const PORTFOLIO_IMAGES = [
  { url: "https://customer-assets-jai6qajn.emergentagent.net/job_1406887e-27f1-4dc9-a01d-472f5d3042b6/artifacts/3occ3nlq_IMG_0448.jpeg", title: "Black GC8 WRX STI" },
  { url: "https://customer-assets-jai6qajn.emergentagent.net/job_1406887e-27f1-4dc9-a01d-472f5d3042b6/artifacts/tr6mfqet_IMG_0458.jpeg", title: "Blue Mazda RX-7 FD" },
  { url: "https://customer-assets-jai6qajn.emergentagent.net/job_1406887e-27f1-4dc9-a01d-472f5d3042b6/artifacts/dlbr9rqm_IMG_0450.jpeg", title: "Grey Blobeye WRX" },
  { url: "https://customer-assets-jai6qajn.emergentagent.net/job_1406887e-27f1-4dc9-a01d-472f5d3042b6/artifacts/jqyyjfzm_IMG_8372.jpeg", title: "White R34 Skyline" },
];

// Metallic Logo Component with mouse-tracking light reflection
const MetallicLogo = () => {
  const logoRef = useRef(null);
  const mouseX = useMotionValue(0.5);
  const mouseY = useMotionValue(0.5);
  
  // Smooth spring animation for the light position
  const springX = useSpring(mouseX, { stiffness: 150, damping: 20 });
  const springY = useSpring(mouseY, { stiffness: 150, damping: 20 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (logoRef.current) {
        const rect = logoRef.current.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width;
        const y = (e.clientY - rect.top) / rect.height;
        mouseX.set(Math.max(0, Math.min(1, x)));
        mouseY.set(Math.max(0, Math.min(1, y)));
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [mouseX, mouseY]);

  return (
    <motion.div 
      ref={logoRef}
      className="relative"
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 1.2, ease: [0.25, 0.4, 0.25, 1] }}
    >
      {/* Glow effect behind logo */}
      <motion.div 
        className="absolute inset-0 blur-3xl opacity-30"
        style={{
          background: `radial-gradient(circle at ${springX.get() * 100}% ${springY.get() * 100}%, rgba(59, 130, 246, 0.4), transparent 60%)`,
        }}
      />
      
      {/* Main logo container with metallic effect */}
      <div className="relative">
        {/* Chrome/metallic gradient overlay that moves with mouse */}
        <motion.div
          className="absolute inset-0 pointer-events-none z-10 mix-blend-overlay"
          style={{
            background: useTransform(
              [springX, springY],
              ([x, y]) => `linear-gradient(${135 + (x - 0.5) * 30}deg, 
                rgba(255,255,255,0) 0%, 
                rgba(255,255,255,0.4) ${40 + (y - 0.5) * 20}%, 
                rgba(255,255,255,0) 60%,
                rgba(200,200,200,0.2) 80%,
                rgba(255,255,255,0) 100%)`
            ),
          }}
        />
        
        {/* Logo text - VELOCITY */}
        <h1 className="font-heading text-7xl sm:text-8xl md:text-9xl lg:text-[12rem] uppercase tracking-tight leading-none text-transparent bg-clip-text"
          style={{
            backgroundImage: `linear-gradient(
              180deg,
              #ffffff 0%,
              #e0e0e0 20%,
              #a0a0a0 40%,
              #c0c0c0 60%,
              #ffffff 80%,
              #d0d0d0 100%
            )`,
            WebkitBackgroundClip: 'text',
            textShadow: '0 4px 30px rgba(255,255,255,0.1)',
          }}
        >
          Velocity
        </h1>
        
        {/* Subtitle */}
        <motion.p 
          className="text-center text-lg sm:text-xl md:text-2xl tracking-[0.5em] uppercase text-zinc-500 font-body mt-2"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.8 }}
        >
          Paint & Panel
        </motion.p>
      </div>
    </motion.div>
  );
};

export default function Logo3DLandingDemo() {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  // Initialize Lenis smooth scroll
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: "vertical",
      smoothWheel: true,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    return () => lenis.destroy();
  }, []);

  const scrollToQuote = () => {
    document.querySelector("#quote-section")?.scrollIntoView({ behavior: "smooth" });
  };

  // Text reveal animation
  const textReveal = {
    hidden: { opacity: 0, y: 40 },
    visible: (delay) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, delay, ease: [0.25, 0.4, 0.25, 1] }
    })
  };

  return (
    <div ref={containerRef} className="bg-[#050505] min-h-screen overflow-x-hidden">
      {/* Demo Banner */}
      <div className="fixed top-0 left-0 right-0 z-[100] bg-gradient-to-r from-[#3b82f6] to-[#8b5cf6] text-white text-center py-2 text-sm font-body">
        ✨ LOGO 3D DEMO - Minimalist + Metallic Style. Move your mouse over the logo!
      </div>

      {/* Hero Section - Full screen logo reveal */}
      <section className="min-h-screen flex flex-col items-center justify-center relative px-6">
        {/* Subtle grid background */}
        <div className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                             linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
            backgroundSize: '100px 100px'
          }}
        />
        
        {/* Radial gradient spotlight */}
        <div className="absolute inset-0 bg-gradient-radial from-zinc-900/50 via-transparent to-transparent opacity-50" />
        
        {/* Location tag */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="mb-12"
        >
          <span className="text-xs tracking-[0.4em] uppercase text-zinc-600 font-body">
            101 Norman St, Woolloongabba, Brisbane
          </span>
        </motion.div>

        {/* Metallic Logo */}
        <MetallicLogo />

        {/* Tagline */}
        <motion.p
          variants={textReveal}
          initial="hidden"
          animate="visible"
          custom={0.8}
          className="text-center text-zinc-500 font-body text-base md:text-lg max-w-xl mt-12 mb-12"
        >
          JDM specialists delivering full resprays, custom paint jobs, and flawless repairs.
          <br />
          <span className="text-zinc-600">No insurance work, just pure craftsmanship.</span>
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          variants={textReveal}
          initial="hidden"
          animate="visible"
          custom={1}
          className="flex flex-col sm:flex-row gap-4"
        >
          <motion.button
            onClick={scrollToQuote}
            className="group relative flex items-center justify-center gap-2 bg-white text-black font-heading text-base tracking-[0.15em] uppercase px-10 py-4 overflow-hidden cursor-pointer border-none"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <motion.div 
              className="absolute inset-0 bg-[#3b82f6]"
              initial={{ x: "-100%" }}
              whileHover={{ x: 0 }}
              transition={{ duration: 0.3 }}
            />
            <span className="relative z-10 group-hover:text-white transition-colors">Get a Quote</span>
            <ChevronRight size={18} className="relative z-10 group-hover:text-white transition-colors" />
          </motion.button>
          
          <motion.button
            onClick={() => navigate("/book")}
            className="flex items-center justify-center gap-2 border border-white/20 text-white font-heading text-base tracking-[0.15em] uppercase px-10 py-4 hover:bg-white/5 hover:border-white/40 transition-all cursor-pointer bg-transparent"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <CalendarDays size={16} />
            Book Appointment
          </motion.button>
          
          <motion.a
            href="tel:0450645926"
            className="flex items-center justify-center gap-2 border border-zinc-800 text-zinc-400 font-heading text-base tracking-[0.15em] uppercase px-10 py-4 hover:text-white hover:border-zinc-600 transition-all no-underline"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Phone size={16} />
            0450 645 926
          </motion.a>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-12 flex flex-col items-center gap-2"
        >
          <span className="text-xs tracking-[0.3em] uppercase text-zinc-600 font-body">Scroll</span>
          <motion.div 
            animate={{ y: [0, 8, 0] }} 
            transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
          >
            <ArrowDown size={16} className="text-zinc-600" />
          </motion.div>
        </motion.div>
      </section>

      {/* Services Section */}
      <section className="py-32 px-6 md:px-12 max-w-[1400px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <h2 className="font-heading text-4xl md:text-6xl uppercase text-white mb-4">Our Services</h2>
          <div className="w-24 h-[1px] bg-gradient-to-r from-transparent via-[#3b82f6] to-transparent mx-auto mb-6" />
          <p className="text-zinc-500 font-body max-w-xl mx-auto">Premium automotive refinishing for JDM enthusiasts and car lovers alike.</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {SERVICES.map((service, i) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              whileHover={{ y: -8, borderColor: "rgba(59, 130, 246, 0.3)" }}
              className="border border-zinc-800/50 bg-gradient-to-b from-zinc-900/50 to-transparent p-8 transition-all duration-500 group cursor-pointer"
            >
              <div className="w-12 h-12 border border-zinc-800 flex items-center justify-center mb-6 group-hover:border-[#3b82f6]/50 group-hover:bg-[#3b82f6]/5 transition-all">
                <service.icon className="w-5 h-5 text-zinc-500 group-hover:text-[#3b82f6] transition-colors" />
              </div>
              <h3 className="font-heading text-xl uppercase text-white mb-3">{service.title}</h3>
              <p className="text-zinc-500 font-body text-sm leading-relaxed">{service.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Portfolio Section */}
      <section className="py-32 overflow-hidden">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20 px-6"
        >
          <h2 className="font-heading text-4xl md:text-6xl uppercase text-white mb-4">Featured Builds</h2>
          <div className="w-24 h-[1px] bg-gradient-to-r from-transparent via-[#3b82f6] to-transparent mx-auto mb-6" />
          <p className="text-zinc-500 font-body">JDM legends brought back to life.</p>
        </motion.div>

        <div className="flex gap-6 px-6 overflow-x-auto pb-8 snap-x snap-mandatory scrollbar-hide">
          {PORTFOLIO_IMAGES.map((img, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.15 }}
              whileHover={{ scale: 1.02 }}
              className="flex-shrink-0 w-[85vw] md:w-[50vw] lg:w-[35vw] aspect-[4/3] relative overflow-hidden snap-center group"
            >
              <img
                src={img.url}
                alt={img.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h3 className="font-heading text-xl uppercase text-white">{img.title}</h3>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section id="quote-section" className="py-32 px-6 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="font-heading text-4xl md:text-6xl lg:text-7xl uppercase text-white mb-6">
            Ready for your
          </h2>
          <h2 className="font-heading text-4xl md:text-6xl lg:text-7xl uppercase mb-8">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#3b82f6] to-[#8b5cf6]">transformation?</span>
          </h2>
          <p className="text-zinc-500 font-body mb-12 max-w-xl mx-auto">
            Get a free quote and join our portfolio of happy customers.
          </p>
          <motion.button
            onClick={() => navigate("/quote")}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-white text-black font-heading text-lg tracking-[0.15em] uppercase px-16 py-5 cursor-pointer border-none hover:bg-[#3b82f6] hover:text-white transition-colors"
          >
            Get a Quote
          </motion.button>
        </motion.div>
      </section>

      {/* Footer spacer */}
      <div className="h-32" />
    </div>
  );
}
